import React from 'react'
import { BsArrow90DegLeft, BsCheck2Circle, BsEye, BsPaperclip } from 'react-icons/bs'
import '../Styles/AdminPage.css'
import dayjs from 'dayjs'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { FetchContext } from '../context/FetchContext'
import AdminPagination from '../components/AdminPagination'

const linky = process.env.REACT_APP_LINKY
const PAGE_LIMIT = 20

const statusLabels = {
    new: 'Ожидает ответа',
    in_progress: 'В работе',
    closed: 'Закрыт администратором',
}

const formatDate = (value) => dayjs(value).format('DD.MM.YYYY HH:mm')
const getFileName = (file) => file?.originalFilename || file?.filename || file?.newFilename || 'Файл'
const getDebtorName = (debtor) => debtor?.fio || debtor?.email || 'Должник'

function FilesModal({
    files,
    label = 'Файлы',
    title = 'Файлы заявки',
    endpoint = 'api/download-request-file',
}) {
    const fetchContext = React.useContext(FetchContext)
    const [show, setShow] = React.useState(false)

    const downloadFile = async (file) => {
        // имя на диске (уникальное) — для запроса, оригинальное — для сохранения
        const storedName = file?.filename || file?.newFilename || getFileName(file)
        const displayName = getFileName(file)
        try {
            const { data } = await fetchContext.authAxios.get(
                `${endpoint}/${encodeURIComponent(storedName)}`,
                { responseType: 'arraybuffer' }
            )
            const blob = new Blob([data])
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = displayName
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 7000)
        } catch (err) {
            console.log(err)
        }
    }

    if (!files?.length) return null

    return (
        <>
            <button type="button" className="adminRequestFilesBtn" onClick={() => setShow(true)}>
                <BsPaperclip />
                <span>{label}: {files.length}</span>
            </button>

            <Modal show={show} onHide={() => setShow(false)} centered dialogClassName="adminFilesModal">
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="adminRequestFilesList">
                        {files.map((file, index) => {
                            const filename = getFileName(file)
                            return (
                                <button type="button" key={`${filename}-${index}`} onClick={() => downloadFile(file)}>
                                    <span>{filename}</span>
                                    <strong>Скачать</strong>
                                </button>
                            )
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShow(false)}>Закрыть</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

function RequestMessages({ messages }) {
    if (!messages?.length) return null

    return (
        <div className="adminNotesThread">
            {messages.map((message) => (
                <div
                    key={message.MessageId}
                    className={`adminNotesMessage adminNotesMessage--${message.Sender === 'admin' ? 'admin' : 'user'}`}
                >
                    <div className="adminNotesMessage__meta">
                        <strong>{message.Sender === 'admin' ? 'Администратор' : 'Должник'}</strong>
                        <time>{formatDate(message.createdAt)}</time>
                    </div>
                    {message.Message && <p>{message.Message}</p>}
                    <FilesModal
                        files={message.File}
                        label="Вложения"
                        title={message.Sender === 'admin' ? 'Файлы ответа' : 'Файлы должника'}
                        endpoint={message.Sender === 'admin' ? 'api/download-notes' : 'api/download-request-file'}
                    />
                </div>
            ))}
        </div>
    )
}

function ProactiveDocumentForm({ onSent }) {
    const fetchContext = React.useContext(FetchContext)
    const [expanded, setExpanded] = React.useState(false)
    const [query, setQuery] = React.useState('')
    const [results, setResults] = React.useState([])
    const [selectedDebtor, setSelectedDebtor] = React.useState(null)
    const [selectedContract, setSelectedContract] = React.useState('')
    const [type, setType] = React.useState('Справка об отсутствии задолженности')
    const [message, setMessage] = React.useState('')
    const [file, setFile] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [searching, setSearching] = React.useState(false)
    const [feedback, setFeedback] = React.useState('')

    React.useEffect(() => {
        const q = query.trim()

        if (q.length < 2 || selectedDebtor) {
            setResults([])
            return undefined
        }

        const timeout = setTimeout(async () => {
            try {
                setSearching(true)
                const { data } = await fetchContext.authAxios.get('api/admin/debtors/search', {
                    params: { q },
                })
                setResults(data || [])
            } catch (error) {
                console.log(error)
                setResults([])
            } finally {
                setSearching(false)
            }
        }, 350)

        return () => clearTimeout(timeout)
    }, [fetchContext.authAxios, query, selectedDebtor])

    const selectDebtor = (debtor) => {
        setSelectedDebtor(debtor)
        setQuery(getDebtorName(debtor))
        setSelectedContract(debtor.contracts?.[0]?.agreementNum || '')
        setResults([])
        setFeedback('')
    }

    const resetSelection = () => {
        setSelectedDebtor(null)
        setSelectedContract('')
        setQuery('')
    }

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!selectedDebtor) {
            setFeedback('Выберите должника')
            return
        }

        if (!message.trim() && !file) {
            setFeedback('Напишите сообщение или прикрепите файл')
            return
        }

        const formData = new FormData()
        formData.append('Email', selectedDebtor.email)
        formData.append('Type', type.trim() || 'Документ от администратора')
        formData.append('Message', message.trim())
        formData.append('DocNumber', selectedContract)
        if (file) formData.append('document', file)

        try {
            setLoading(true)
            const { data } = await fetchContext.authAxios1.post('api/admin/account-requests/send', formData)
            setFeedback(data.message || 'Документ отправлен')
            setMessage('')
            setFile(null)
            onSent?.()
        } catch (error) {
            console.log(error)
            setFeedback(error.response?.data?.message || 'Не удалось отправить документ')
        } finally {
            setLoading(false)
        }
    }

    return (
        <section className={`adminProactiveDoc${expanded ? ' adminProactiveDoc--expanded' : ''}`}>
            <div className="adminProactiveDoc__head">
                <div>
                    <h2>Отправить документ должнику</h2>
                    <p>Можно отправить справку или любой файл в личный кабинет без предварительного запроса.</p>
                </div>
                <button type="button" onClick={() => setExpanded((current) => !current)}>
                    {expanded ? 'Свернуть' : 'Отправить документ'}
                </button>
            </div>

            {expanded && <form className="adminProactiveDoc__form" onSubmit={handleSubmit}>
                <div className="adminProactiveDoc__search">
                    <label>Должник</label>
                    <div className="adminProactiveDoc__searchBox">
                        <input
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value)
                                setSelectedDebtor(null)
                            }}
                            placeholder="ФИО, email, договор или project_id"
                        />
                        {selectedDebtor && (
                            <button type="button" onClick={resetSelection}>Сбросить</button>
                        )}
                    </div>
                    {searching && <span className="adminProactiveDoc__hint">Поиск...</span>}
                    {results.length > 0 && (
                        <div className="adminProactiveDoc__results">
                            {results.map((debtor) => (
                                <button type="button" key={debtor.email} onClick={() => selectDebtor(debtor)}>
                                    <strong>{getDebtorName(debtor)}</strong>
                                    <span>{debtor.email}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="adminProactiveDoc__grid">
                    <label>
                        <span>Тип документа</span>
                        <input value={type} onChange={(event) => setType(event.target.value)} />
                    </label>
                    <label>
                        <span>Договор</span>
                        <select value={selectedContract} onChange={(event) => setSelectedContract(event.target.value)}>
                            <option value="">Без договора</option>
                            {(selectedDebtor?.contracts || []).map((contract) => (
                                <option key={`${contract.UidSale}-${contract.agreementNum}`} value={contract.agreementNum}>
                                    {contract.agreementNum || `project_id: ${contract.UidSale}`}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Сообщение для должника"
                    rows={2}
                />

                <div className="adminProactiveDoc__actions">
                    <label className="adminNotesUploadBtn">
                        <BsPaperclip />
                        <span>{file?.name || 'Прикрепить файл'}</span>
                        <input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                    </label>
                    <button type="submit" className="adminProactiveDoc__send" disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : <><BsArrow90DegLeft /> Отправить</>}
                    </button>
                </div>

                {feedback && <p className="adminProactiveDoc__feedback">{feedback}</p>}
            </form>}
        </section>
    )
}

function NotesRequestCard({
    item,
    onOpenChat,
}) {
    const messages = item.Messages || []
    const lastMessage = messages[messages.length - 1]
    const lastMessageText = lastMessage?.Message || (lastMessage?.File?.length ? 'Прикреплены файлы' : item.Question || 'Сообщений пока нет')

    return (
        <article className="adminRequestCard adminRequestCard--accountResponse">
            <header className="adminRequestCard__header">
                <div>
                    <span className="adminRequestCard__id">Запрос №{item.ReqId}</span>
                    <span className="adminRequestCard__source adminRequestCard__source--account">
                        {statusLabels[item.Status] || statusLabels.new}
                    </span>
                    <h3>{item.Name || 'Клиент не указан'}</h3>
                </div>
                <time>{formatDate(item.LastMessageAt || item.createdAt)}</time>
            </header>

            <div className="adminRequestCard__meta">
                <div className="adminRequestField adminRequestField--topic">
                    <span>Тема</span>
                    <strong>{item.Type || 'Без темы'}</strong>
                </div>
                {item.Email && (
                    <div className="adminRequestField">
                        <span>Email</span>
                        <strong>{item.Email}</strong>
                    </div>
                )}
                {item.DocNumber && (
                    <div className="adminRequestField">
                        <span>Договор</span>
                        <strong>{item.DocNumber}</strong>
                    </div>
                )}
            </div>

            <div className="adminRequestCard__chatSummary">
                <div>
                    <span>{lastMessage?.Sender === 'admin' ? 'Последний ответ администратора' : 'Последнее сообщение должника'}</span>
                    <strong>{lastMessageText}</strong>
                </div>
                <button type="button" onClick={() => onOpenChat(item)}>
                    Открыть чат
                </button>
            </div>

            <div className="adminRequestCard__compactMeta">
                <span>{messages.length} сообщ.</span>
                <FilesModal files={item.File} label="Вложения должника" />
            </div>
        </article>
    )
}

function AdminPageNotes() {
    const fetchContext = React.useContext(FetchContext)
    const [requests, setRequests] = React.useState([])
    const [sorted, setSorted] = React.useState({ reversed: false })
    const [searchPhrase, setSearchPhrase] = React.useState('')
    const [debouncedSearch, setDebouncedSearch] = React.useState('')
    const [page, setPage] = React.useState(1)
    const [pagination, setPagination] = React.useState({
        page: 1,
        limit: PAGE_LIMIT,
        total: 0,
        totalPages: 1,
    })
    const [dataLoading, setDataLoading] = React.useState(false)
    const [comments, setComments] = React.useState({})
    const [documents, setDocuments] = React.useState({})
    const [loadingId, setLoadingId] = React.useState(null)
    const [closingId, setClosingId] = React.useState(null)
    const [activeChat, setActiveChat] = React.useState(null)

    const getQuestionData = React.useCallback(async () => {
        try {
            setDataLoading(true)
            const { data } = await fetchContext.authAxios.get('api/notes-request', {
                timeout: 12000,
                params: {
                    page,
                    limit: PAGE_LIMIT,
                    search: debouncedSearch,
                    sort: sorted.reversed ? 'old' : 'new',
                },
            })

            if (Array.isArray(data)) {
                setRequests(data)
                setPagination({
                    page: 1,
                    limit: data.length || PAGE_LIMIT,
                    total: data.length,
                    totalPages: 1,
                })
                return
            }

            setRequests(data.rows || [])
            setPagination({
                page: data.page || page,
                limit: data.limit || PAGE_LIMIT,
                total: data.total || 0,
                totalPages: data.totalPages || 1,
            })
        } catch (err) {
            console.log(err)
        } finally {
            setDataLoading(false)
        }
    }, [debouncedSearch, fetchContext, page, sorted.reversed])

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchPhrase.trim()), 350)
        return () => clearTimeout(timer)
    }, [searchPhrase])

    React.useEffect(() => {
        getQuestionData()
    }, [getQuestionData])

    const handleSearch = (event) => {
        setSearchPhrase(event.target.value)
        setPage(1)
    }

    const sortByDate = (reversed) => {
        setSorted({ reversed })
        setPage(1)
    }

    const handleCommentChange = (id, value) => {
        setComments((current) => ({ ...current, [id]: value }))
    }

    const handleFileChange = (id, file) => {
        setDocuments((current) => ({ ...current, [id]: file || null }))
    }

    const handleSubmit = async (event, id) => {
        event.preventDefault()
        const file = documents[id]
        const text = (comments[id] || '').trim()

        if (!file && !text) {
            alert('Напишите ответ или прикрепите файл')
            return
        }

        const formData = new FormData()
        if (file) formData.append('document', file)
        if (text) formData.append('comment', text)

        try {
            setLoadingId(id)
            const { data } = await fetchContext.authAxios1.post(`api/upload-notes/${id}`, formData)
            setComments((current) => ({ ...current, [id]: '' }))
            setDocuments((current) => ({ ...current, [id]: null }))
            if (data.request) {
                setRequests((current) => current.map((item) => (item.ReqId === id ? data.request : item)))
                setActiveChat(data.request)
            } else {
                await getQuestionData()
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Не удалось отправить ответ пользователю')
        } finally {
            setLoadingId(null)
        }
    }

    const handleClose = async (id) => {
        const closeMessage = window.prompt('Комментарий при закрытии чата. Можно оставить пустым.')
        if (closeMessage === null) return

        try {
            setClosingId(id)
            await fetchContext.authAxios.post(`api/account-requests/${id}/close`, {
                message: closeMessage,
            })
            setRequests((current) => current.filter((item) => item.ReqId !== id))
            setActiveChat(null)
        } catch (error) {
            alert(error.response?.data?.message || 'Не удалось закрыть чат')
        } finally {
            setClosingId(null)
        }
    }

    return (
        <React.Suspense>
            <div className="AdminContainer adminRequestsPage">
                <div className="adminRequestsToolbar">
                    <div>
                        <h1>Обращения из ЛК</h1>
                        <p>Открытые чаты из личного кабинета и отправка документов должнику.</p>
                    </div>
                    <div className="adminRequestsToolbar__controls">
                        <input
                            type="search"
                            placeholder="Поиск по заявке, ФИО, email, договору"
                            value={searchPhrase}
                            onChange={handleSearch}
                        />
                        <select
                            value={sorted.reversed ? 'old' : 'new'}
                            onChange={(event) => sortByDate(event.target.value === 'old')}
                        >
                            <option value="new">Сначала новые</option>
                            <option value="old">Сначала старые</option>
                        </select>
                        {linky && (
                            <a href={linky} target="__blank" className="adminRequestsToolbar__link" title="Открыть внешний ресурс">
                                <BsEye />
                            </a>
                        )}
                    </div>
                </div>

                <ProactiveDocumentForm onSent={getQuestionData} />

                <div className="adminRequestsSummary">
                    <span>Открыто: {requests.length}</span>
                    <span>Всего: {pagination.total}</span>
                </div>

                <AdminPagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={setPage}
                />

                <div className="AdminFeed adminRequestsList">
                    {dataLoading ? (
                        <div className="adminRequestsEmpty">
                            <Spinner animation="border" variant="success" />
                        </div>
                    ) : requests.length ? (
                        requests.map((item) => (
                            <NotesRequestCard
                                key={item.ReqId}
                                item={item}
                                onOpenChat={setActiveChat}
                            />
                        ))
                    ) : (
                        <div className="adminRequestsEmpty">Открытых чатов из личного кабинета нет</div>
                    )}
                </div>

                <Modal
                    show={Boolean(activeChat)}
                    onHide={() => setActiveChat(null)}
                    centered
                    dialogClassName="adminChatModal"
                >
                    {activeChat && (
                        <>
                            <Modal.Header closeButton>
    <div className="adminChatModal__title">
        <span>Запрос №{activeChat.ReqId}</span>
        <h3>{activeChat.Type || 'Чат личного кабинета'}</h3>
        {activeChat.Name && (
            <p>
                {activeChat.Name}
                {activeChat.BirthDate && (
                    <> · {dayjs(activeChat.BirthDate).format('DD.MM.YYYY')}</>
                )}
            </p>
        )}
        {activeChat.DocNumber && <p>Договор: {activeChat.DocNumber}</p>}
    </div>
</Modal.Header>
                            <Modal.Body>
                                <RequestMessages messages={activeChat.Messages} />

                                {!activeChat.Messages?.length && (
                                    <div className="adminRequestCard__question">
                                        <span>Сообщение пользователя</span>
                                        <p>{activeChat.Question || 'Комментарий не указан'}</p>
                                        <FilesModal files={activeChat.File} label="Вложения" />
                                    </div>
                                )}

                                <form className="adminNotesResponseForm adminNotesResponseForm--modal" onSubmit={(event) => handleSubmit(event, activeChat.ReqId)}>
                                    <textarea
                                        value={comments[activeChat.ReqId] || ''}
                                        onChange={(event) => handleCommentChange(activeChat.ReqId, event.target.value)}
                                        placeholder="Ответ пользователю в личный кабинет"
                                        rows={2}
                                    />

                                    <div className="adminNotesResponseForm__actions">
                                        <label className="adminNotesUploadBtn">
                                            <BsPaperclip />
                                            <span>{documents[activeChat.ReqId]?.name || 'Прикрепить файл'}</span>
                                            <input type="file" onChange={(event) => handleFileChange(activeChat.ReqId, event.target.files?.[0])} />
                                        </label>
                                        <button type="submit" className="adminNotesSendBtn" disabled={loadingId === activeChat.ReqId}>
                                            {loadingId === activeChat.ReqId ? <Spinner animation="border" size="sm" /> : <BsArrow90DegLeft />}
                                        </button>
                                        <button
                                            type="button"
                                            className="adminNotesCloseBtn"
                                            disabled={closingId === activeChat.ReqId}
                                            onClick={() => handleClose(activeChat.ReqId)}
                                        >
                                            {closingId === activeChat.ReqId ? <Spinner animation="border" size="sm" /> : <><BsCheck2Circle /> Закрыть чат</>}
                                        </button>
                                    </div>
                                </form>
                            </Modal.Body>
                        </>
                    )}
                </Modal>
            </div>
        </React.Suspense>
    )
}

export default React.memo(AdminPageNotes)
